import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { UserNotificationApiService, UserNotificationModel } from './user-notification-api.service';

/**
 * Ports legacy `UserNotificationService` — holds the current global user-notification
 * banner message (if any) as a `BehaviorSubject`, with a "preview" override used by the
 * admin `UserNotificationComponent` to show what the banner will look like before saving.
 */
@Injectable({ providedIn: 'root' })
export class UserNotificationService {
  private readonly userNotificationApiService = inject(UserNotificationApiService);

  /** The current user notification, if any. While the preview is active, the preview
   *  notification is returned instead. */
  readonly userNotificationSubject = new BehaviorSubject<UserNotificationModel | null>(null);
  isPreviewActive = false;

  /** Refreshes and returns the current user notification, if any. If the preview is
   *  active, no refresh is performed and the preview notification is returned. */
  async refresh(): Promise<UserNotificationModel | null> {
    if (this.isPreviewActive) {
      return this.userNotificationSubject.getValue();
    }

    const userNotification = await firstValueFrom(this.userNotificationApiService.get());

    if (this.isPreviewActive) {
      return this.userNotificationSubject.getValue();
    }

    this.userNotificationSubject.next(userNotification ?? null);
    return userNotification ?? null;
  }

  async set(userNotification: UserNotificationModel): Promise<void> {
    const request: UserNotificationModel = {
      IsEnabled: userNotification.IsEnabled,
      Start: userNotification.Start,
      End: userNotification.End,
      Content: userNotification.Content,
    };

    await firstValueFrom(this.userNotificationApiService.update(request));
    this.userNotificationSubject.next(userNotification);
  }

  setPreview(userNotification: UserNotificationModel): void {
    this.isPreviewActive = true;
    this.userNotificationSubject.next(userNotification);
  }

  /** Turns off the preview and refreshes the current user notification. */
  unsetPreview(): void {
    if (!this.isPreviewActive) {
      return;
    }

    this.isPreviewActive = false;
    void this.refresh();
  }
}
